import { booleanAttribute, ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { DsTagModule } from '@bmw-ds/components';

import { EnumTranslationPath } from '../../../i18n/model/translation';
import { useI18n } from '../../../i18n/services/i18n.service';
import { CdfLinkComponent } from '../../../profile-page/components/email/cdf-link.component';
import { AddressField } from '../../model/address-field.model';
import { CommunicationField } from '../../model/communication-field.model';
import { CustomerContact } from '../../model/customer-information.model';
import { AddressViewService } from '../../services/address.view-service';
import { FeatureVisibilityService } from '../../services/feature-visibility.service';
import { InfoCellComponent } from '../info-cell/info-cell.component';

// Not an enum, to ease use in the template
type ContactField = 'address' | 'email' | 'mobile' | 'phone';

@Component({
  selector: 'cdf-customer-contact-line',
  host: {
    '[style.--column-count]': 'columnCount()',
  },
  imports: [InfoCellComponent, CdfLinkComponent, DsTagModule],
  template: `
    <cdf-info-cell [title]="i18n.enum('address', contactTranslationPath())" [content]="addressString() ?? ''" subgrid />
    <cdf-info-cell
      [title]="i18n.enum('email', contactTranslationPath())"
      [hidden]="featureVisibilityService.isHidden(CommunicationField.EMAIL)"
      subgrid
    >
      <cdf-link prefix="mailto" [value]="contact().email" />
      @if (showBmwMiniId()) {
        <div class="bmw-mini-id">
          @if (contact().bmwMiniId; as bmwMiniId) {
            @if (bmwMiniId === contact().email) {
              <ds-tag tone="positive" icon="check">{{ i18n('bmwOrMiniId') }}</ds-tag>
            } @else {
              <ds-tag icon="check">{{ i18n('bmwOrMiniId') }}:</ds-tag>
              <cdf-link prefix="mailto" [value]="contact().bmwMiniId" />
            }
          } @else {
            <ds-tag tone="critical" icon="close">{{ i18n('bmwOrMiniId') }}</ds-tag>
          }
        </div>
      }
    </cdf-info-cell>
    @for (field of phoneFields(); track $index) {
      <cdf-info-cell [title]="i18n.enum(field, contactTranslationPath())" subgrid>
        <cdf-link prefix="tel" [value]="contact()[field]" />
      </cdf-info-cell>
    }
  `,
  styleUrl: './customer-contact-line.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomerContactLineComponent {
  readonly addressViewService = inject(AddressViewService);
  readonly featureVisibilityService = inject(FeatureVisibilityService);
  protected readonly i18n = useI18n();

  readonly contact = input.required<CustomerContact>();
  readonly showBmwMiniId = input(false, { transform: booleanAttribute });
  readonly business = input(false, { transform: booleanAttribute });
  readonly singlePhone = input(false, { transform: booleanAttribute });
  readonly enableLinks = input(false, { transform: booleanAttribute });

  protected readonly addressString = computed(() => {
    const address = this.contact().address;
    const includeProvince = !this.featureVisibilityService.isHidden(AddressField.PROVINCE);
    return address && this.addressViewService.getAsStringWithOptionalFields(address, address.country, includeProvince);
  });

  protected readonly phoneFields = computed<('phone' | 'mobile')[]>(() => {
    const theoreticallyAvailable: ('phone' | 'mobile')[] = [];
    if (!this.featureVisibilityService.isHidden(CommunicationField.PHONE)) {
      theoreticallyAvailable.push('phone');
    }
    if (!this.featureVisibilityService.isHidden(CommunicationField.MOBILE_PHONE)) {
      theoreticallyAvailable.push('mobile');
    }
    const trulyAvailable = theoreticallyAvailable.filter((field) => this.contact()[field]);

    if (!this.singlePhone()) {
      return theoreticallyAvailable;
    } else if (trulyAvailable.includes('mobile')) {
      return ['mobile'];
    } else if (trulyAvailable.includes('phone')) {
      return ['phone'];
    } else if (theoreticallyAvailable.includes('mobile')) {
      return ['mobile'];
    } else if (theoreticallyAvailable.includes('phone')) {
      return ['phone'];
    } else {
      return [];
    }
  });

  protected readonly contactTranslationPath = computed<EnumTranslationPath<ContactField>>(() =>
    this.business() ? 'businessContact' : 'genericContact',
  );

  protected readonly columnCount = computed(() => (this.singlePhone() ? 3 : 4));

  // Split the email at the @-sign, to allow for long emails to wrap
  protected readonly emailParts = computed(() => this.contact().email?.split(/(?=@)/) ?? []);

  protected readonly CommunicationField = CommunicationField;
}
